import React, { useState, useRef } from 'react';
import { Plus, Filter, Upload, Sparkles, Download, Search } from 'lucide-react';
import { Button } from './Button';
import { PersonaCard } from './PersonaCard';
import { PersonaCreateDialog } from './PersonaCreateDialog';
import { PersonaGenerateDialog } from './PersonaGenerateDialog';
import { PersonaImportDialog } from './PersonaImportDialog';
import { Skeleton } from './Skeleton';
import {
  usePersonas,
  useUpdatePersonaSetting,
  useCreatePersona,
  useUpdatePersona,
  useDeletePersona,
  useImportPersona,
  useExportPersona,
  useGeneratePersona,
} from '../helpers/usePersonasApi';
import { PersonaWithSettings } from '../endpoints/personas/list_GET.schema';
import { PersonaCategoryArrayValues } from '../helpers/schema';
import { toast } from 'sonner';
import styles from './UserSettingsPersonasTab.module.css';

type CategoryFilter = string;

export const UserSettingsPersonasTab: React.FC = () => {
  const [filter, setFilter] = useState<CategoryFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [editingPersona, setEditingPersona] = useState<PersonaWithSettings | null>(null);

  const { data, isLoading } = usePersonas();
  const updateSetting = useUpdatePersonaSetting();
  const createPersona = useCreatePersona();
  const updatePersona = useUpdatePersona();
  const deletePersona = useDeletePersona();
  const importPersona = useImportPersona();
  const exportPersona = useExportPersona();
  const generatePersona = useGeneratePersona();

  // Build filter buttons dynamically from actual data + built-in defaults
  const builtInLabels: Record<string, string> = {
    development: 'Development',
    security: 'Security',
    operations: 'Operations',
    documentation: 'Docs',
    compliance: 'Compliance',
    governance: 'Governance',
    custom: 'Custom',
  };

  const filterButtons = React.useMemo(() => {
    const buttons: { key: CategoryFilter; label: string }[] = [{ key: 'all', label: 'All' }];
    const builtInSet = new Set(PersonaCategoryArrayValues as string[]);

    // Add built-in categories first
    for (const cat of PersonaCategoryArrayValues) {
      buttons.push({ key: cat, label: builtInLabels[cat] || cat });
    }

    // Add user-created categories from actual data
    if (data?.personas) {
      const customCats = new Set<string>();
      for (const p of data.personas) {
        if (!builtInSet.has(p.category)) {
          customCats.add(p.category);
        }
      }
      for (const cat of Array.from(customCats).sort()) {
        const label = cat.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        buttons.push({ key: cat, label });
      }
    }

    return buttons;
  }, [data?.personas]);

  // Collect all existing categories for the create dialog
  const existingCategories = React.useMemo(() => {
    if (!data?.personas) return [];
    return Array.from(new Set(data.personas.map((p) => p.category)));
  }, [data?.personas]);

  const filteredPersonas = React.useMemo(() => {
    if (!data?.personas) return [];
    let result = data.personas;

    // Apply category filter
    if (filter !== 'all') {
      result = result.filter((p) => p.category === filter);
    }

    // Apply search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(query) ||
          p.category?.toLowerCase().includes(query) ||
          p.description?.toLowerCase().includes(query) ||
          p.tags?.some((t) => t.toLowerCase().includes(query)),
      );
    }

    return result;
  }, [data?.personas, filter, searchQuery]);

  const enabledCount = data?.personas.filter((p) => p.isEnabled).length ?? 0;

  const handleToggle = (personaId: string, enabled: boolean) => {
    updateSetting.mutate({ personaId, isEnabled: enabled });
  };

  const handleCreateOrUpdate = (formData: {
    name: string;
    description?: string;
    icon: string;
    category: string;
    systemPrompt: string;
    priority: number;
    tags?: string[];
  }) => {
    if (editingPersona) {
      updatePersona.mutate(
        {
          id: editingPersona.id,
          ...formData,
        },
        {
          onSuccess: () => {
            setEditingPersona(null);
            setCreateDialogOpen(false);
          },
        },
      );
    } else {
      createPersona.mutate(formData as any, {
        onSuccess: () => {
          setCreateDialogOpen(false);
        },
      });
    }
  };

  const handleEdit = (persona: PersonaWithSettings) => {
    setEditingPersona(persona);
    setCreateDialogOpen(true);
  };

  const handleDelete = (personaId: string) => {
    deletePersona.mutate(personaId);
  };

  const handleExport = async (personaId: string) => {
    try {
      const result = await exportPersona.mutateAsync(personaId);
      // Download as JSON file
      const blob = new Blob([JSON.stringify(result.poml, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${result.poml.name.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.poml.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Persona exported successfully');
    } catch (error) {
      // Error is handled by the hook
    }
  };

  const handleImport = (pomlData: any, importSource?: string) => {
    importPersona.mutate(
      { poml: pomlData, importSource },
      {
        onSuccess: () => {
          setImportDialogOpen(false);
        },
      },
    );
  };

  const handleGenerate = (prompt: string, category?: string) => {
    generatePersona.mutate(
      { prompt, category },
      {
        onSuccess: () => {
          setGenerateDialogOpen(false);
        },
      },
    );
  };

  const handleDialogClose = (open: boolean) => {
    setCreateDialogOpen(open);
    if (!open) {
      setEditingPersona(null);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingGrid}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className={styles.skeletonCard} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerInfo}>
          <h3 className={styles.title}>AI Personas</h3>
          <p className={styles.subtitle}>
            Enable personas to customize AI behavior. {enabledCount} active.
          </p>
        </div>
        <div className={styles.headerActions}>
          <Button variant="outline" size="sm" onClick={() => setImportDialogOpen(true)}>
            <Upload size={14} />
            Import
          </Button>
          <Button variant="outline" size="sm" onClick={() => setGenerateDialogOpen(true)}>
            <Sparkles size={14} />
            Generate
          </Button>
          <Button onClick={() => setCreateDialogOpen(true)} size="sm">
            <Plus size={14} />
            Create
          </Button>
        </div>
      </div>

      <div className={styles.searchBar}>
        <Search size={14} className={styles.searchIcon} />
        <input
          type="text"
          placeholder="Search personas by name, category, description, or tag..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={styles.searchInput}
        />
        {searchQuery && (
          <button
            className={styles.searchClear}
            onClick={() => setSearchQuery('')}
            aria-label="Clear search"
          >
            &times;
          </button>
        )}
      </div>

      <div className={styles.filters}>
        <Filter size={14} className={styles.filterIcon} />
        {filterButtons.map((btn) => (
          <button
            key={btn.key}
            className={`${styles.filterButton} ${filter === btn.key ? styles.active : ''}`}
            onClick={() => setFilter(btn.key)}
          >
            {btn.label}
          </button>
        ))}
      </div>

      {filteredPersonas.length === 0 ? (
        <div className={styles.empty}>
          <p>
            {searchQuery
              ? `No personas match "${searchQuery}"${filter !== 'all' ? ` in this category` : ''}.`
              : 'No personas found in this category.'}
          </p>
          {searchQuery ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSearchQuery('')}
              className={styles.emptyButton}
            >
              Clear search
            </Button>
          ) : filter === 'custom' ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCreateDialogOpen(true)}
              className={styles.emptyButton}
            >
              Create your first custom persona
            </Button>
          ) : null}
        </div>
      ) : (
        <div className={styles.grid}>
          {filteredPersonas.map((persona) => (
            <PersonaCard
              key={persona.id}
              persona={persona}
              onToggle={handleToggle}
              onEdit={!persona.isBuiltin ? handleEdit : undefined}
              onDelete={!persona.isBuiltin ? handleDelete : undefined}
              onExport={() => handleExport(persona.id)}
              isToggling={updateSetting.isPending}
            />
          ))}
        </div>
      )}

      <PersonaCreateDialog
        open={createDialogOpen}
        onOpenChange={handleDialogClose}
        editingPersona={editingPersona}
        existingCategories={existingCategories}
        onSubmit={handleCreateOrUpdate}
        isSubmitting={createPersona.isPending || updatePersona.isPending}
      />

      <PersonaGenerateDialog
        open={generateDialogOpen}
        onOpenChange={setGenerateDialogOpen}
        onGenerate={handleGenerate}
        isGenerating={generatePersona.isPending}
      />

      <PersonaImportDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        onImport={handleImport}
        isImporting={importPersona.isPending}
      />
    </div>
  );
};
